import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table, Button, Input, Space, Typography, Card, Row, Col, Tag, Select,
  Dropdown, Checkbox, message, Modal, Tooltip, Avatar, Badge, Statistic,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  PhoneOutlined, WhatsAppOutlined, StarOutlined, StarFilled, FilterOutlined,
  DownloadOutlined, SettingOutlined, ReloadOutlined, UserAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchLeads, deleteLead, toggleFavorite, convertLeadToClient, fetchPropertyTypes, fetchLeadStats, patchLead,
} from '../services/leadService';
import { fetchSettings } from '../services/settingsService';
import LeadFormDrawer from './LeadFormDrawer';
import { PRIORITY_COLORS, BRAND, colorMap, leadAgeDays, scoreColor } from './leadConstants';

const { Text, Title } = Typography;

const ALL_COLUMNS = [
  'LeadNo', 'Name', 'Mobile', 'City', 'PropertyType', 'Source', 'Priority',
  'Status', 'NextFollowupAt', 'AssignedUser', 'LastActivityAt', 'createdAt', 'actions',
];
const DEFAULT_HIDDEN = ['AssignedUser', 'LastActivityAt'];

const fmtDate = (d) => (d ? dayjs(d).format('DD MMM YYYY') : '—');
const fmtDateTime = (d) => (d ? dayjs(d).format('DD MMM, hh:mm A') : '—');

const Leads = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const initFilters = {};
  ['Priority', 'Status', 'Source', 'PropertyType', 'City'].forEach((k) => {
    const v = searchParams.get(k); if (v) initFilters[k] = v;
  });
  const [filters, setFilters] = useState(initFilters);
  const [stats, setStats] = useState({});

  // Re-apply filters when the query string changes (deep links from dashboards)
  useEffect(() => {
    const next = {};
    ['Priority', 'Status', 'Source', 'PropertyType', 'City'].forEach((k) => {
      const v = searchParams.get(k); if (v) next[k] = v;
    });
    setFilters(next); setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [settings, setSettings] = useState({});
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [hiddenCols, setHiddenCols] = useState(DEFAULT_HIDDEN);

  const priorityColors = useMemo(
    () => ({ ...PRIORITY_COLORS, ...colorMap(settings.priority_color) }),
    [settings]
  );
  const statusColors = useMemo(() => colorMap(settings.followup_status), [settings]);

  const loadMeta = useCallback(async () => {
    try {
      const [s, pt] = await Promise.all([fetchSettings(), fetchPropertyTypes()]);
      setSettings(s);
      setPropertyTypes(pt);
    } catch { /* settings are non-critical for listing */ }
  }, []);

  const loadStats = useCallback(async () => {
    try { setStats(await fetchLeadStats()); } catch { /* ignore */ }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchLeads({ page, pageSize, search, sortBy, sortDir, ...filters });
      setData(res.data);
      setTotal(res.total);
    } catch (e) {
      message.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, sortBy, sortDir, filters]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadLeads(); loadStats(); }, [loadLeads, loadStats]);

  const openCreate = () => { setEditingLead(null); setDrawerOpen(true); };
  const openEdit = (lead) => { setEditingLead(lead); setDrawerOpen(true); };

  const onSaved = () => { setDrawerOpen(false); loadLeads(); loadStats(); };

  const handleDelete = (lead) => {
    Modal.confirm({
      title: 'Delete lead',
      content: `Move "${lead.Name}" to trash? You can restore it later.`,
      okText: 'Delete', okButtonProps: { danger: true }, cancelText: 'Cancel',
      onOk: async () => {
        try { await deleteLead(lead.LeadID); message.success('Lead deleted'); loadLeads(); loadStats(); }
        catch { message.error('Failed to delete lead'); }
      },
    });
  };

  const handleFavorite = async (lead) => {
    try { await toggleFavorite(lead.LeadID); loadLeads(); }
    catch { message.error('Failed to update'); }
  };

  const handleInline = async (record, field, value) => {
    if (value === record[field]) return;
    try {
      await patchLead(record.LeadID, { [field]: value });
      // optimistic local update so the row reflects immediately
      setData(prev => prev.map(r => r.LeadID === record.LeadID ? { ...r, [field]: value } : r));
      message.success(`${field} updated`);
      loadStats();
    } catch { message.error('Update failed'); }
  };

  const handleConvert = (lead) => {
    Modal.confirm({
      title: 'Convert to client',
      content: `Create a client record from "${lead.Name}"?`,
      okText: 'Convert', cancelText: 'Cancel',
      onOk: async () => {
        try { await convertLeadToClient(lead.LeadID); message.success('Converted to client'); loadLeads(); loadStats(); }
        catch (e) { message.error(e.response?.data?.error || 'Failed to convert'); }
      },
    });
  };

  const exportCsv = async () => {
    try {
      const res = await fetchLeads({ page: 1, pageSize: 10000, search, sortBy, sortDir, ...filters });
      const rows = res.data || [];
      if (!rows.length) { message.info('Nothing to export'); return; }
      const cols = ['LeadNo', 'Name', 'Mobile', 'AltMobile', 'Email', 'City', 'PropertyType',
        'Source', 'Priority', 'Status', 'AssignedUser', 'NextFollowupAt', 'createdAt'];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `leads_${dayjs().format('YYYYMMDD_HHmm')}.csv`; a.click();
      URL.revokeObjectURL(url);
      message.success(`Exported ${rows.length} leads`);
    } catch { message.error('Export failed'); }
  };

  const handleTableChange = (pagination, _f, sorter) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (sorter && sorter.field) {
      setSortBy(sorter.field);
      setSortDir(sorter.order === 'ascend' ? 'asc' : 'desc');
    }
  };

  const setFilter = (key, value) => {
    setPage(1);
    setFilters(prev => {
      const next = { ...prev };
      if (value === undefined || value === null || value === '') delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const allColumns = [
    {
      title: '#', dataIndex: 'LeadNo', key: 'LeadNo', width: 70, sorter: true, fixed: 'left',
      render: (no, r) => (
        <Space size={4}>
          {r.IsFavorite ? <StarFilled style={{ color: '#faad14', fontSize: 12 }} /> : null}
          <Text strong style={{ color: BRAND }}>{no}</Text>
        </Space>
      ),
    },
    {
      title: 'Lead Name', dataIndex: 'Name', key: 'Name', sorter: true, width: 230,
      render: (text, r) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate(`/leads/${r.LeadID}`)}>
          <Avatar style={{ backgroundColor: priorityColors[r.Priority] || BRAND, marginRight: 10 }}>
            {text ? text.charAt(0).toUpperCase() : '?'}
          </Avatar>
          <div>
            <Text strong style={{ display: 'block' }}>{text}</Text>
            <Tooltip title="Lead score">
              <Badge color={scoreColor(r.LeadScore)} text={<Text type="secondary" style={{ fontSize: 12 }}>Score {r.LeadScore} · {leadAgeDays(r.createdAt)}d old</Text>} />
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      title: 'Mobile', dataIndex: 'Mobile', key: 'Mobile', width: 150,
      render: (m, r) => (
        <div>
          <div><PhoneOutlined style={{ color: '#1890ff', marginRight: 6 }} />{m}</div>
          {r.AltMobile && <Text type="secondary" style={{ fontSize: 12 }}>{r.AltMobile}</Text>}
        </div>
      ),
    },
    { title: 'City', dataIndex: 'City', key: 'City', width: 120, render: (t) => t || '—' },
    { title: 'Property', dataIndex: 'PropertyType', key: 'PropertyType', width: 120, render: (t) => t || '—' },
    { title: 'Source', dataIndex: 'Source', key: 'Source', width: 120, render: (t) => t ? <Tag>{t}</Tag> : '—' },
    {
      title: 'Priority', dataIndex: 'Priority', key: 'Priority', width: 130, sorter: true,
      render: (p, r) => (
        <Select
          size="small" variant="borderless" value={p || undefined} placeholder="—"
          style={{ width: '100%' }} onClick={(e) => e.stopPropagation()}
          onChange={(v) => handleInline(r, 'Priority', v)}
          options={(settings.priority_color || []).map(o => ({
            value: o.Value,
            label: <Tag color={priorityColors[o.Value] || 'default'} style={{ borderRadius: 12 }}>{o.Label}</Tag>,
          }))}
        />
      ),
    },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 170, sorter: true,
      render: (s, r) => (
        <Select
          size="small" variant="borderless" value={s || undefined} placeholder="—" showSearch
          style={{ width: '100%' }} onClick={(e) => e.stopPropagation()}
          onChange={(v) => handleInline(r, 'Status', v)}
          options={[{ value: 'New', label: <Tag color="blue">New</Tag> },
            ...(settings.followup_status || []).map(o => ({
              value: o.Value, label: <Tag color={statusColors[o.Value] || 'blue'}>{o.Label}</Tag>,
            }))]}
        />
      ),
    },
    {
      title: 'Next Follow-up', dataIndex: 'NextFollowupAt', key: 'NextFollowupAt', width: 160, sorter: true,
      render: (d) => {
        if (!d) return <Text type="secondary">—</Text>;
        const overdue = dayjs(d).isBefore(dayjs());
        return <Text style={{ color: overdue ? '#f5222d' : undefined }}>{fmtDateTime(d)}</Text>;
      },
    },
    { title: 'Assigned', dataIndex: 'AssignedUser', key: 'AssignedUser', width: 120, render: (t) => t || '—' },
    { title: 'Last Activity', dataIndex: 'LastActivityAt', key: 'LastActivityAt', width: 150, render: fmtDateTime },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 130, sorter: true, render: fmtDate },
    {
      title: 'Actions', key: 'actions', width: 200, fixed: 'right', align: 'center',
      render: (_, r) => (
        <Space size={0} wrap={false}>
          <Tooltip title="View"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/leads/${r.LeadID}`)} /></Tooltip>
          <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
          <Tooltip title="Call"><Button type="text" size="small" icon={<PhoneOutlined />} href={`tel:${r.Mobile}`} /></Tooltip>
          <Tooltip title="WhatsApp"><Button type="text" size="small" icon={<WhatsAppOutlined style={{ color: '#25D366' }} />} href={`https://wa.me/${(r.Mobile || '').replace(/[^0-9]/g, '')}`} target="_blank" /></Tooltip>
          <Tooltip title={r.IsFavorite ? 'Unpin' : 'Pin'}><Button type="text" size="small" icon={r.IsFavorite ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />} onClick={() => handleFavorite(r)} /></Tooltip>
          {!r.ConvertedClientID && <Tooltip title="Convert to client"><Button type="text" size="small" icon={<UserAddOutlined />} onClick={() => handleConvert(r)} /></Tooltip>}
          <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r)} /></Tooltip>
        </Space>
      ),
    },
  ];

  const columns = allColumns.filter(c => !hiddenCols.includes(c.key));

  const columnMenu = {
    items: ALL_COLUMNS.filter(k => k !== 'LeadNo' && k !== 'actions').map(key => {
      const col = allColumns.find(c => c.key === key);
      return {
        key,
        label: (
          <Checkbox
            checked={!hiddenCols.includes(key)}
            onChange={(e) => setHiddenCols(prev => e.target.checked ? prev.filter(k => k !== key) : [...prev, key])}
          >
            {typeof col?.title === 'string' ? col.title : key}
          </Checkbox>
        ),
      };
    }),
  };

  // Clicking a summary card applies the matching filter to the table below.
  const applyCardFilter = (patch) => {
    setPage(1);
    setFilters(patch);
    document.querySelector('.lead-table-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const statCard = (title, value, color, filter) => (
    <Col xs={12} sm={8} md={6} lg={4} xl={3} key={title}>
      <Card bodyStyle={{ padding: '12px 16px' }} style={{ borderRadius: 8, cursor: filter ? 'pointer' : 'default' }}
        hoverable={!!filter} onClick={filter ? () => applyCardFilter(filter) : undefined}>
        <Statistic title={title} value={value || 0} valueStyle={{ color, fontSize: 20 }} />
      </Card>
    </Col>
  );

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '4px 8px' }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Lead Management</Title>
          <Text type="secondary">Track and nurture every enquiry</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ borderRadius: 6 }}>
            Add Lead
          </Button>
        </Col>
      </Row>

      {/* Summary cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {statCard('Total', stats.totalLeads, BRAND, {})}
        {statCard('New', stats.newLeads, '#1890ff', { Status: 'New' })}
        {statCard('Hot', stats.hot, '#f5222d', { Priority: 'Hot' })}
        {statCard('Warm', stats.warm, '#fa8c16', { Priority: 'Warm' })}
        {statCard('Cold', stats.cold, '#1890ff', { Priority: 'Cold' })}
        {statCard('Converted', stats.converted, '#52c41a', { Priority: 'Converted' })}
        {statCard('Conversion %', `${stats.conversionRate || 0}%`, '#722ed1')}
      </Row>

      <Card bordered={false} className="lead-table-card" style={{ borderRadius: 8 }} bodyStyle={{ padding: 16 }}>
        {/* Toolbar */}
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }} justify="space-between">
          <Col xs={24} md={8}>
            <Input.Search
              placeholder="Search name, mobile, email, city..."
              allowClear enterButton={<SearchOutlined />}
              onSearch={(v) => { setPage(1); setSearch(v); }}
              onChange={(e) => { if (!e.target.value) { setSearch(''); setPage(1); } }}
            />
          </Col>
          <Col xs={24} md={16}>
            <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Select allowClear placeholder="Priority" style={{ width: 120 }}
                onChange={(v) => setFilter('Priority', v)}
                options={(settings.priority_color || []).map(p => ({ value: p.Value, label: p.Label }))} />
              <Select allowClear placeholder="Status" style={{ width: 150 }}
                onChange={(v) => setFilter('Status', v)} showSearch
                options={[{ value: 'New', label: 'New' }, ...(settings.followup_status || []).map(s => ({ value: s.Value, label: s.Label }))]} />
              <Select allowClear placeholder="Source" style={{ width: 130 }}
                onChange={(v) => setFilter('Source', v)} showSearch
                options={(settings.lead_source || []).map(s => ({ value: s.Value, label: s.Label }))} />
              <Select allowClear placeholder="Property" style={{ width: 130 }}
                onChange={(v) => setFilter('PropertyType', v)} showSearch
                options={propertyTypes.map(p => ({ value: p, label: p }))} />
              <Dropdown menu={columnMenu} trigger={['click']} placement="bottomRight">
                <Button icon={<SettingOutlined />}>Columns</Button>
              </Dropdown>
              <Tooltip title="Export to CSV"><Button icon={<DownloadOutlined />} onClick={exportCsv} /></Tooltip>
              <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={() => { loadLeads(); loadStats(); }} /></Tooltip>
            </Space>
          </Col>
        </Row>

        <Table
          dataSource={data}
          columns={columns}
          rowKey="LeadID"
          loading={loading}
          size="small"
          bordered
          scroll={{ x: 1500 }}
          onChange={handleTableChange}
          pagination={{
            current: page, pageSize, total,
            showSizeChanger: true, showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} leads`,
          }}
        />
      </Card>

      <LeadFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={onSaved}
        editingLead={editingLead}
        settings={settings}
        propertyTypes={propertyTypes}
      />
    </div>
  );
};

export default Leads;
